/**
 * Besichtigungen — Kalender-Übersicht + Termin-Anlage
 * ====================================================
 * Zeigt eine Wochenansicht aller Besichtigungs-Termine der Kanzlei.
 * Ein Klick auf einen Termin öffnet den Detail-Dialog (Bearbeiten / Absagen /
 * Einladung senden / .ics herunterladen).
 * Ein Klick auf einen leeren Slot öffnet den Anlage-Dialog.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarBlank, Plus, ArrowLeft, ArrowRight, Envelope, Download, Trash, X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate } from "@/lib/constants";
import { ProspectActions } from "@/components/marketing/ListingWorkspace";

const STATUS_LABELS = {
  scheduled: "Geplant",
  confirmed: "Bestätigt",
  done: "Durchgeführt",
  cancelled: "Abgesagt",
};
const STATUS_CLS = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  done: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

// Helper: date manipulation
function startOfWeek(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // Mo = 0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function addDays(d, n) { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt; }
function fmtISODate(d) { return d.toISOString().slice(0, 10); }
function fmtTimeShort(iso) {
  try { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function Besichtigungen() {
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");

  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [items, setItems] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode: 'new'|'edit', data? }

  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: v }, { data: p }, { data: l }] = await Promise.all([
        api.get(`/viewings?from_date=${fmtISODate(weekStart)}&to_date=${fmtISODate(weekEnd)}T23:59:59`),
        api.get("/prospects"),
        api.get("/public-listings"),
      ]);
      setItems(v || []);
      setProspects(p || []);
      setListings(l || []);
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((v) => {
      const key = (v.scheduled_at || "").slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(v);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")));
    return map;
  }, [items]);

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const goToday = () => setWeekStart(startOfWeek(new Date()));

  return (
    <div className="space-y-4" data-testid="besichtigungen-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <CalendarBlank size={24} className="text-emerald-700" />
            Besichtigungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Woche vom <b>{formatDate(fmtISODate(weekStart))}</b> bis {formatDate(fmtISODate(addDays(weekStart, 6)))}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={prevWeek}><ArrowLeft size={12} /></Button>
          <Button size="sm" variant="outline" onClick={goToday}>Heute</Button>
          <Button size="sm" variant="outline" onClick={nextWeek}><ArrowRight size={12} /></Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                  onClick={() => setDialog({ mode: "new", data: { scheduled_at: `${fmtISODate(new Date())}T10:00`, duration_min: 30, prospect_ids: [], status: "scheduled" } })}
                  data-testid="besichtigung-new-btn">
            <Plus size={12} /> Neuer Termin
          </Button>
        </div>
      </div>

      {/* Wochen-Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
        {Array.from({ length: 7 }).map((_, i) => {
          const d = addDays(weekStart, i);
          const key = fmtISODate(d);
          const dayItems = byDay[key] || [];
          const isToday = fmtISODate(new Date()) === key;
          const isWeekend = i >= 5;
          return (
            <div
              key={key}
              className={`rounded-md border p-2 min-h-[140px] ${isToday ? "border-emerald-400 bg-emerald-50/30" : (isWeekend ? "border-slate-200 bg-slate-50/40" : "border-slate-200 bg-white")}`}
              data-testid={`day-${key}`}
            >
              <div className="text-[11px] font-semibold text-slate-500 mb-2 flex items-center justify-between">
                <span>{d.toLocaleDateString("de-DE", { weekday: "short" })}</span>
                <span className={`tabular-nums ${isToday ? "text-emerald-700 font-bold" : ""}`}>{d.getDate()}.{d.getMonth() + 1}.</span>
              </div>
              <div className="space-y-1.5">
                {dayItems.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setDialog({ mode: "edit", data: v })}
                    className={`w-full text-left rounded p-1.5 border ${STATUS_CLS[v.status] || STATUS_CLS.scheduled} ${highlightId === v.id ? "ring-2 ring-amber-400" : ""} hover:opacity-90`}
                    data-testid={`viewing-${v.id}`}
                  >
                    <div className="text-[10px] font-mono font-semibold">{fmtTimeShort(v.scheduled_at)}</div>
                    <div className="text-[10px] truncate">{v.location || "—"}</div>
                    <div className="text-[9px] opacity-75 truncate">
                      {(v.prospect_ids || []).length} Bewerber
                    </div>
                  </button>
                ))}
                {dayItems.length === 0 && (
                  <div className="text-[10px] text-slate-300 italic text-center py-2">—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dialog && (
        <ViewingDialog
          mode={dialog.mode}
          data={dialog.data}
          prospects={prospects}
          listings={listings}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function ViewingDialog({ mode, data, prospects, listings, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    scheduled_at: (data?.scheduled_at || "").slice(0, 16),
    duration_min: data?.duration_min || 30,
    location: data?.location || "",
    notes: data?.notes || "",
    status: data?.status || "scheduled",
    prospect_ids: data?.prospect_ids || [],
    listing_id: data?.listing_id || "",
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = mode === "edit";

  const togglProspect = (pid) => {
    set("prospect_ids", form.prospect_ids.includes(pid)
      ? form.prospect_ids.filter((x) => x !== pid)
      : [...form.prospect_ids, pid]);
  };

  const save = async () => {
    if (!form.scheduled_at) { toast.error("Bitte Datum/Uhrzeit angeben"); return; }
    setSaving(true);
    try {
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() };
      if (isEdit) await api.patch(`/viewings/${data.id}`, payload);
      else await api.post("/viewings", payload);
      toast.success(isEdit ? "Termin gespeichert" : "Termin angelegt");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!await window._domivusConfirm("Termin wirklich löschen?")) return;
    try { await api.delete(`/viewings/${data.id}`); toast.success("Gelöscht"); onSaved(); }
    catch { toast.error("Fehler"); }
  };

  const sendInvite = async () => {
    if (!isEdit || form.prospect_ids.length === 0) {
      toast.error("Bitte mindestens einen Bewerber zuordnen und speichern.");
      return;
    }
    try {
      const { data: r } = await api.post(`/viewings/${data.id}/send-invite`);
      toast.success(`${r.queued} Einladung(en) versendet`);
      onSaved();
    } catch { toast.error("Versand fehlgeschlagen"); }
  };

  const downloadIcal = () => {
    const url = `${api.defaults.baseURL}/viewings/${data.id}/ical`;
    window.open(url, "_blank");
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="viewing-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Termin bearbeiten" : "Neuer Besichtigungstermin"}</DialogTitle>
          <DialogDescription>
            Legen Sie Datum, Ort und die zugeordneten Bewerber fest. Nach dem Speichern können Sie eine Info-E-Mail mit .ics-Anhang versenden.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Datum & Uhrzeit *</Label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="viewing-when" />
            </div>
            <div>
              <Label>Dauer (Min.)</Label>
              <Input type="number" min="10" max="480" value={form.duration_min} onChange={(e) => set("duration_min", Number(e.target.value) || 30)} data-testid="viewing-duration" />
            </div>
          </div>
          <div>
            <Label>Ort</Label>
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="z. B. Musterstr. 5, Wohnung 3. OG" data-testid="viewing-location" />
          </div>
          <div>
            <Label>Zugeordnetes Inserat</Label>
            <Select value={form.listing_id || "__none__"} onValueChange={(v) => set("listing_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="viewing-listing"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— (keins) —</SelectItem>
                {listings.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.title || l.public_slug}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Bewerber (mehrere möglich)</Label>
            <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
              {prospects.filter((p) => p.status !== "rejected" && p.status !== "withdrawn").length === 0 && (
                <div className="text-xs text-slate-400 italic text-center py-2">Keine offenen Bewerber</div>
              )}
              {prospects.filter((p) => p.status !== "rejected" && p.status !== "withdrawn").map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={form.prospect_ids.includes(p.id)}
                    onChange={() => togglProspect(p.id)}
                    data-testid={`viewing-prospect-${p.id}`}
                  />
                  <span>{p.first_name} {p.last_name}</span>
                  <span className="text-xs text-slate-500 truncate">· {p.email}</span>
                  <span className="ml-auto shrink-0"><ProspectActions id={p.id} compact onAction={onClose} /></span>
                </label>
              ))}
            </div>
          </div>
          {isEdit && (
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="viewing-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Interne Notiz</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="viewing-notes" />
          </div>
          {isEdit && (
            <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-500 space-y-0.5">
              {data.invite_sent_at && <div>✓ Einladung versendet: {formatDate(data.invite_sent_at)}</div>}
              {data.remind_sent_at && <div>✓ Reminder gesetzt: {formatDate(data.remind_sent_at)}</div>}
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {isEdit && (
            <>
              <Button variant="outline" onClick={downloadIcal} className="gap-1" data-testid="viewing-download-ical">
                <Download size={12} /> .ics
              </Button>
              <Button variant="outline" onClick={sendInvite} className="gap-1" data-testid="viewing-send-invite">
                <Envelope size={12} /> Einladung senden
              </Button>
              <Button variant="outline" onClick={remove} className="gap-1 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="viewing-delete">
                <Trash size={12} /> Löschen
              </Button>
            </>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="viewing-save">
            {saving ? "Speichere…" : (isEdit ? "Speichern" : "Anlegen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
